import React, { useId } from 'react';
import { Button, InputNumber } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { COLUMNS } from '../../utils/formGrid/limits';
import { spanAllowed, widthChoices, widthLimits, widthState, widthSummary, limitsNote } from '../../utils/formWidth';

/**
 * One element's width and new-row, on the form layout grid (KPE-023): Auto
 * (with the width it resolves to), the quick picks 1/4 · 1/3 · 1/2 · 2/3 ·
 * 3/4 · Full, a stepper of columns out of twelve, and "Start on a new row".
 *
 * It asks the vendored limits table (utils/formWidth) what this element's
 * type can hold, so a choice outside the limits is disabled and the sentence
 * under the controls says why ("Rich text needs at least half the row.").
 * Disabled buttons cannot take focus, so that sentence is always on screen
 * rather than only a hover title; each disabled pick carries its own reason
 * as a title too.
 *
 * A width stored outside the limits (a type changed after it was set) is not
 * hidden: the control says what is stored and what the form draws, and offers
 * to take the drawn width or Auto. The card's own save never depends on it.
 *
 * Plain props, no layout state of its own — the editor owns the body:
 *
 *   <FormWidthControl element={def} span="auto" newRow={false}
 *                     onChange={patch => …} />
 *
 * `onChange` is called with `{ span }` (a whole number of columns, or 'auto')
 * or `{ newRow }`. `disabled` shows the controls as they stand without
 * letting them change (an inherited layout not yet customized); `notice` is
 * a line that says why, in place of the editing hint.
 */
export default function FormWidthControl({
	element,
	span = 'auto',
	newRow = false,
	onChange,
	disabled = false,
	notice = null,
	className = '',
}) {
	const uid = useId().replace(/:/g, '');
	const limits = widthLimits(element);
	const state = widthState(element, span);
	const choices = widthChoices(limits);
	const noteId = `form-width-note-${uid}`;
	const stepperId = `form-width-columns-${uid}`;
	const label = element?.label?.singular || element?.name || 'field';

	const setSpan = next => { if (!disabled) onChange?.({ span: next }); };

	return (
		<div className={`form-width ${className}`.trim()} role="group" aria-label={`Width of ${label}`}>
			<div className="form-width__head">
				<span className="form-width__title">Width</span>
				<span className="form-width__readout" aria-live="polite">{widthSummary(state, newRow)}</span>
			</div>

			<div className="form-width__picks" role="group" aria-label="Width choices" aria-describedby={noteId}>
				{choices.map(choice => {
					const selected = choice.span === 'auto' ? state.auto : !state.auto && state.span === choice.span;
					return (
						<span key={choice.key} className="form-width__pick" title={choice.reason || undefined}>
							<Button
								size="small"
								className={selected ? 'form-width__button form-width__button--on' : 'form-width__button'}
								aria-pressed={selected}
								disabled={disabled || choice.disabled}
								onClick={() => setSpan(choice.span)}
							>
								{choice.label}
							</Button>
						</span>
					);
				})}
			</div>

			<div className="form-width__stepper">
				<label htmlFor={stepperId} className="form-width__stepper-label">Columns</label>
				<InputNumber
					id={stepperId}
					size="small"
					min={limits.min}
					max={limits.max}
					step={1}
					precision={0}
					value={state.auto ? state.drawn : state.fix}
					disabled={disabled}
					aria-describedby={noteId}
					onChange={value => {
						// Only a whole number the element can hold: typing passes
						// through values on the way (a "1" on the way to "12").
						if (spanAllowed(limits, value) && (state.auto || value !== state.fix)) setSpan(value);
					}}
				/>
				<span className="form-width__stepper-of">of {COLUMNS}</span>
			</div>

			<p className="form-width__note" id={noteId}>{notice || limitsNote(limits)}</p>

			{state.flag ? (
				<div className="form-width__flag" role="status">
					<span className="form-width__flag-text">{state.flag}</span>
					{disabled ? null : (
						<span className="form-width__flag-actions">
							<Button size="small" onClick={() => setSpan(state.fix)}>Use {state.fix} of {COLUMNS}</Button>
							<Button size="small" onClick={() => setSpan('auto')}>Use Auto</Button>
						</span>
					)}
				</div>
			) : null}

			<ToggleRow
				className="form-width__new-row"
				label="Start on a new row"
				description="Begins this field on a fresh row instead of filling the row before it."
				checked={newRow}
				disabled={disabled}
				id={`form-width-newrow-${uid}`}
				onChange={value => { if (!disabled) onChange?.({ newRow: value === true }); }}
			/>
		</div>
	);
}
